/**
 * ScriptContextMenu
 *
 * Right-click context menu for script items in the Scripts panel.
 * Provides Open, Pin/Unpin, Associate Connection, Copy Path, Reveal, Remove.
 */

import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '../../../components/ui/context-menu';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { useScriptBrowserStore } from '../store/scriptBrowserStore';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { openScriptInTab } from '../services/scriptBrowserApi';
import type { ScriptEntry } from '../types/scriptBrowserTypes';
import { safeConnectionName } from '../../../types/safeConnectionDisplay';

type ScriptContextMenuProps = {
  script: ScriptEntry;
  isPinned?: boolean;
  children: ReactNode;
};

export function ScriptContextMenu({
  script,
  isPinned,
  children,
}: ScriptContextMenuProps) {
  const togglePin = useScriptBrowserStore((s) => s.togglePin);
  const removeScript = useScriptBrowserStore((s) => s.removeScript);
  const restoreScript = useScriptBrowserStore((s) => s.restoreScript);
  const setConnectionAssociation = useScriptBrowserStore(
    (s) => s.setConnectionAssociation
  );
  const savedConnections = useConnectionStore((s) => s.connections);

  const handleOpen = async () => {
    const result = await openScriptInTab(script.filePath);
    if (result === null) {
      toast.error({
        title: 'Failed to open script',
        description: script.filePath,
      });
    }
  };

  const handleTogglePin = () => {
    togglePin(script.id);
  };

  const handleCopyPath = async () => {
    const ok = await writeClipboardText(script.filePath);
    if (ok) {
      toast.success({ title: 'Path copied to clipboard' });
    } else {
      toast.error({ title: 'Failed to copy path' });
    }
  };

  const handleRevealInFinder = async () => {
    if (!window.electronAPI?.showItemInFolder) return;
    const result = await window.electronAPI.showItemInFolder(script.filePath);
    if (!result.success) {
      toast.error({
        title: 'Could not reveal file',
        description: result.error,
      });
    }
  };

  const handleRemove = () => {
    const entry = script;
    removeScript(entry.id);
    toast.default({
      title: `Removed ${entry.fileName}`,
      duration: 6000,
      action: {
        label: 'Undo',
        onClick: () => restoreScript(entry),
      },
    });
  };

  const handleSetConnection = (
    connectionId?: string,
    connectionName?: string
  ) => {
    setConnectionAssociation(script.id, connectionId, connectionName);
  };

  const isMac =
    typeof navigator !== 'undefined' &&
    navigator.platform?.toLowerCase().includes('mac');

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        <ContextMenuItem onSelect={handleOpen}>Open</ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={handleTogglePin}>
          {isPinned ? 'Unpin' : 'Pin'}
        </ContextMenuItem>
        <ContextMenuSub>
          <ContextMenuSubTrigger>
            Associate with Connection
          </ContextMenuSubTrigger>
          <ContextMenuSubContent>
            <ContextMenuItem
              onSelect={() => handleSetConnection(undefined, undefined)}
            >
              None
            </ContextMenuItem>
            {savedConnections.length > 0 && <ContextMenuSeparator />}
            {savedConnections.map((conn) => {
              const displayName = safeConnectionName(conn);
              return (
                <ContextMenuItem
                  key={conn.id}
                  onSelect={() => handleSetConnection(conn.id, displayName)}
                >
                  {displayName}
                  {script.connectionId === conn.id && ' *'}
                </ContextMenuItem>
              );
            })}
          </ContextMenuSubContent>
        </ContextMenuSub>
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={handleCopyPath}>
          Copy File Path
        </ContextMenuItem>
        <ContextMenuItem onSelect={handleRevealInFinder}>
          {isMac ? 'Reveal in Finder' : 'Show in Explorer'}
        </ContextMenuItem>
        <ContextMenuSeparator />
        <ContextMenuItem
          onSelect={handleRemove}
          className="text-destructive focus:text-destructive"
        >
          Remove from List
        </ContextMenuItem>
      </ContextMenuContent>
    </ContextMenu>
  );
}
